import logger from '../../utils/logger-rum';
import React, {
	useMemo,
	useCallback,
	useState,
	useRef,
	useEffect,
	useContext,
} from 'react';
import {
	useDispatch,
	useSelector,
} from 'react-redux';
import { useMachine } from '@xstate/react';
import Socket from '../../connection/socket';
import { GreenroomState as GS } from '../../stores/types/types';
import { ROOM_STATES } from '../../stores/reducers/settings';
import type {
	MediaItem,
	PagedMediaItemExisting,
	PagedMediaItemNew,
	PresentedMediaItem,
	VideoState,
} from '../../stores/types/media';

import useEchoCancellation, { shouldDoEchoCancel } from '../../hooks/use-echo-cancellation';
import useShots from '../../contexts/shots/provider';
import * as MediaActions from '../../stores/actions/media';
import { findMediaPageBy } from '../../utils/media';
import { showAlert } from '../general-components/alert-service';
import {
	videoMachine,
	actions,
	videoActionCommands,
	initialContext,
} from '../../utils/video-machine';

import usePrevious from '../../utils/use-previous';
import {
	getShotFeedVideoInfo,
	getVideoDuration,
	VideoFeedTypes,
} from '../../utils/media';
import { ResourcePoolContext } from '../../hooks/use-resource-pool';
import {
	useVideoMachine,
	VideoMachineActionType,
} from '../../contexts/video-machine';

import { MediaControlsContext } from '../../contexts/media-controls';
import type { Shot } from '../../stores/types/shots';
import { isAudio, isVideo } from '../../utils/utils';
import { useTimingLeader } from '../../hooks/use-timing-leader';
import { scheduledStreamActiveStates } from '../../connection/scheduled-streams';

const {
	LOADED,
	FAIL,
	TIMING,
	TIMECHANGE,
	MUTE,
	UNMUTE,
	VOLUME,
	PLAY,
	PAUSE,
	END,
} = videoActionCommands;

interface MediaStateUpdate {
	paused?: boolean,
	timeElapsed?: number | null,
	volume?: string,
	muted?: boolean,
	mediaPage?: PagedMediaItemExisting | null,
}

interface Props {
	mediaElRef: React.RefObject<HTMLVideoElement | null> | React.RefObject<HTMLAudioElement | null>;
	mediaId: number;
	socket?: Socket | null;
	selectedMediaFeed?: number;
	onSelectMediaFeedChange?: React.Dispatch<React.SetStateAction<number | undefined>>;
	isForMainVideoEditingComponent?: boolean;
	iterateLoadedMediaCount?: Function;
	isEditingFeed: boolean;
	isForShotEditorModal?: boolean;
	audioOnly?: boolean;
	showbuilderMediaItem?: PresentedMediaItem;
	updateShowbuilderMedia?: Function;
	mediaItem?: MediaItem;
	echoCancelAudioRef: React.RefObject<HTMLAudioElement | null>;
	presentedMediaItem?: PresentedMediaItem;
	isPreloading?: boolean;
	src: string;
}

enum LoadedStatus {
	Loading,
	Loaded,
	Failed,
}

const doEchoCancel = shouldDoEchoCancel();

export default function UseSyncMedia(props: Props) {
	const {
		mediaElRef,
		mediaId,
		isForMainVideoEditingComponent,
		iterateLoadedMediaCount,
		isEditingFeed = false,
		audioOnly,
		showbuilderMediaItem,
		updateShowbuilderMedia = () => {},
		echoCancelAudioRef,
		presentedMediaItem,
		src,
	} = props;
	const playWasPressed = useRef(false);

	const isForShotEditorModal = props.isForShotEditorModal ? props.isForShotEditorModal : false;
	const session = useSelector((state: GS) => state.SessionReducer.session);
	const roomState = useSelector((state: GS) => state.MeetingReducer.roomState);
	const media = useSelector((state: GS) => state.MediaReducer.media);
	const mediaPages = useSelector((state: GS) => state.MediaReducer.mediaPages);
	const presentedMedia = useSelector((state: GS) => state.MediaReducer.presentedMedia);
	const shots = useSelector((state: GS) => state.ShotsReducer.shots);
	const shotStartTimestampEntity = useSelector(
		(state: GS) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);
	const embed = useSelector((state: GS) => state.SessionReducer.embed);
	const broadcasting = useSelector((state: GS) => state.BroadcastReducer.broadcasting);
	const isTimingLeader = useTimingLeader();
	const scheduledStream = useSelector((state: GS) => state.MeetingReducer.scheduledStream);
	const backstageCanvasVolume = useSelector(
		(state: GS) => state.MeetingReducer.backstageCanvasVolume,
	);
	const token = useSelector(
		(state: GS) => state.SessionReducer.token,
	);
	const meeting = useSelector(
		(state: GS) => state.SessionReducer.meeting,
	);

	const {
		currentShot,
		isOnCanvas,
		handleSelectShot,
	} = useShots();

	const {
		shot_id: shotId,
		shot_data: {
			shotFeeds,
			autoplay,
			autoDuration,
		},
	} = currentShot;

	useEchoCancellation(
		mediaElRef,
		echoCancelAudioRef,
	);

	const { addMediaControls, removeMediaControlsById } = useContext(MediaControlsContext);

	const [duration, setDuration] = useState<number>(0);
	const lastPlayTime = useRef(0);
	const [playbackWarningShown, setPlaybackWarningShown] = useState<boolean>(false);
	const [readyForInput, setReadyForInput] = useState(false);

	const dispatch = useDispatch();
	const showVideoPlaybackWarning = useRef(false);
	const resourcePool = useContext(ResourcePoolContext);
	const { videoMachineDispatch } = useVideoMachine();

	const initialLoad = useRef<boolean>(true);
	const start = useRef<number | null>(null);
	// used to sync the start time of new videos with paginated media
	const forceFireSyncOnAutoplay = useRef<boolean>(false);
	const [mediaLoadedStatus, setMediaLoadedStatus] = useState<LoadedStatus>(LoadedStatus.Loading);

	const currentPageIsVideo = useMemo(() => isVideo(src), [src]);
	const currentPageIsAudio = useMemo(() => isAudio(src), [src]);

	const mediaIsOnCanvas = useMemo(() => (
		shotFeeds.some((feed) => feed.media === mediaId)
	), [shotFeeds, mediaId]);

	const mediaWasOnCanvas = usePrevious(mediaIsOnCanvas);

	const mediaLeftCanvas = mediaWasOnCanvas && !mediaIsOnCanvas;
	const mediaEnteredCanvas = !mediaWasOnCanvas && mediaIsOnCanvas;
	const syncVidState = useMemo(
		() => presentedMediaItem?.videoState,
		[presentedMediaItem?.videoState],
	);

	const currentMediaPage = useMemo(
		() => findMediaPageBy(mediaPages, src, presentedMediaItem?.mediaId),
		[mediaPages, src, presentedMediaItem?.mediaId],
	);

	const currentMediaPageVolume = useMemo((): number => {
		const curVolume = currentMediaPage?.volume;

		if (Number.isNaN(Number(curVolume)) || curVolume === null) {
			return 1;
		}

		return Number(curVolume);
	}, [currentMediaPage?.volume]);

	const [current, send] = useMachine(videoMachine, {
		actions: { ...actions },
		context: {
			...initialContext,
			shownVolume: currentMediaPageVolume,
		},
	});

	useEffect(() => () => {
		removeMediaControlsById(`${mediaId}${isForShotEditorModal ? '-draft' : ''}`);
	}, [mediaId]);

	const {
		elapsed,
		paused,
		shownVolume,
		video,
		muted: videoMachineMuted,
	} = current.context;
	const muted = isForShotEditorModal
		? true
		: currentMediaPage?.muted ?? videoMachineMuted;

	// our timer timmy
	const timmy = useRef<NodeJS.Timer>(null);

	const adjustedVolume = useCallback(
		(volumeIncoming: number) => (
			(isOnCanvas || embed) ? volumeIncoming : volumeIncoming * backstageCanvasVolume
		),
		[backstageCanvasVolume, isOnCanvas, embed],
	);

	const isStreaming = roomState === 1 && broadcasting;
	const shouldFire = (!syncVidState || forceFireSyncOnAutoplay.current) && (embed || (!isStreaming && isTimingLeader));
	
	useEffect(()=>{
		if(playWasPressed.current) {
			playWasPressed.current = false;
		}
	}, [playWasPressed.current]);

	useEffect(() => {
		if (mediaLeftCanvas) {
			send(PAUSE, { from: 'mediaLeftCanvas' });
			send(TIMECHANGE, { currentTime: 0, duration, from: 'mediaLeftCanvas' });
		} else if (mediaEnteredCanvas) {
			// The embed view should always be in synchronized state since it's there from the beginning of the show.
			// So the embed view will actually be the primary timingLeader meaning a producer will only be a timingLeader
			// if the room isn't currently streaming/recording and the room is in a live state.
			
			send(PLAY, { from: 'mediaEnteredCanvas', doFireSync: shouldFire });
		}
	}, [mediaLeftCanvas, mediaEnteredCanvas, shouldFire]);

	useEffect(() => {
		if (mediaLoadedStatus === LoadedStatus.Loading) {
			if (mediaElRef.current?.readyState ?? 0 >= 2) {
				setMediaLoadedStatus(LoadedStatus.Loaded);
			}
		}
	}, [mediaElRef.current?.readyState]);

	useEffect(() => {
		if (!currentMediaPage?.muted) {
			send(VOLUME, {
				shownVolume, adjustedVolume, from: 'update-volume-useEfffect',
			});
		}
	}, [isOnCanvas, backstageCanvasVolume, shownVolume]);

	function checkVideoPlaybackLogic() {
		if (!paused) {
			// @ts-ignore
			if (
				mediaElRef.current
				&& mediaElRef.current.currentTime === lastPlayTime.current
				&& mediaElRef.current.ended) {
				if (!showVideoPlaybackWarning.current) {
					// don't need to keep setting it
					showVideoPlaybackWarning.current = true;
				}
			} else if (showVideoPlaybackWarning.current) { // update it back to not show video is playing
				showVideoPlaybackWarning.current = false;
			}
			// @ts-ignore
			if (mediaElRef.current) { lastPlayTime.current = mediaElRef.current.currentTime; }
		} else {
			showVideoPlaybackWarning.current = false;
		}
	}

	useEffect(() => {
		if (!mediaElRef.current?.paused && !timmy.current) {
			lastPlayTime.current = 0.1;
			// @ts-ignore
			timmy.current = setInterval(checkVideoPlaybackLogic, 5000);
		} else if (mediaElRef.current?.paused && timmy.current) {
			clearInterval(timmy.current);
			showVideoPlaybackWarning.current = false;
			// @ts-ignore
			timmy.current = null;
		}
	}, [mediaElRef.current?.paused]);

	useEffect(() => {
		if (!mediaElRef.current?.paused && showVideoPlaybackWarning.current && !playbackWarningShown) {
			showAlert({
				message: 'Could not play video locally',
				description: 'The video is playing normally in the broadcast',
				duration: 7000,
				type: 'warning',
			});

			// send the log
			logger.warn('showing alert for video playback failure');

			// we have shown the alert
			setPlaybackWarningShown(true);

			// ensure we don't have a memory leak
			const timeOut = setInterval(() => {
				setPlaybackWarningShown(false);
				clearInterval(timeOut);
			// show this only every 30 seconds so it doesn't repeat a bunch after start and stopping
			}, 30 * 1000);
		}
	}, [showVideoPlaybackWarning.current]);

	useEffect(() => {
		const asyncGetDuration = async () => {
			const videoElm = document.createElement('video');
			videoElm.preload = 'metadata';
			videoElm.crossOrigin = 'anonymous';
			const extension = src.split('.').pop();
			const mediaType = extension === 'm3u8' ? 'hls' : '';
			let videoDuration = 0;
			if (resourcePool) {
				videoDuration = await getVideoDuration(src, mediaType, videoElm, resourcePool);
			}
			setDuration(videoDuration);
		};
		asyncGetDuration();
	}, [src]);

	const updatePage = (
		updatedVolume?: string,
		updatedMuteState?: boolean,
		mediaPage?: PagedMediaItemExisting | null,
		from?: string,
	) => {
		const meetingIdNumber = meeting?.meeting;
		const updatedVolumeNumber = updatedVolume !== undefined && !Number.isNaN(Number(updatedVolume))
			? Number(updatedVolume)
			: null;

		// Avoid the `0 / null / undefined === false` issue
		const hasVolumeUpdate = updatedVolumeNumber !== null;
		const hasMuteUpdate = updatedMuteState !== undefined;

		if (
			token
			&& meetingIdNumber
			&& session
			&& (hasVolumeUpdate || hasMuteUpdate)
		) {
			let mediaPageErrorStatus: string = 'media page not found, did not backfill';

			if (mediaPage) {
				const mediaPageUpdate: PagedMediaItemExisting = {
					...mediaPage,
					...(hasVolumeUpdate && { volume: updatedVolumeNumber }),
					...(hasMuteUpdate && { muted: updatedMuteState }),
				};

				dispatch(MediaActions.saveMediaPage(
					token,
					meetingIdNumber,
					mediaPageUpdate,
					session,
				));

				mediaPageErrorStatus = '';
			} else if (
				presentedMediaItem?.mediaId
				&& src
				&& from
				&& !['loading video', 'volume onMouseDown'].includes(from)
			) {
				/*
					Logging note:
					This should not happen, but keeping it the same for now
					to ensure this works and look at logs. If no logs are
					being generated, then it can be removed.
				*/
				const mediaPageAdd: PagedMediaItemNew = {
					mediaId: presentedMediaItem.mediaId,
					meeting: meetingIdNumber,
					url: src,
					volume: hasVolumeUpdate ? updatedVolumeNumber : 1,
					muted: hasMuteUpdate ? updatedMuteState : false,
				};

				dispatch(MediaActions.saveMediaPage(
					token,
					meetingIdNumber,
					mediaPageAdd,
					session,
				));

				mediaPageErrorStatus = 'media page not found, but was backfilled';
			}

			// See logging note above
			if (mediaPageErrorStatus) {
				/*
					We expect mediaPage to be found since it's set when the
					media item is uploaded. If it's not found, it would be
					good to know the values used here.
				*/
				logger.error('feed-media.tsx => updatePage => mediaPage not found', {
					meetingIdNumber,
					volume: updatedVolumeNumber,
					muted: updatedMuteState,
					from: from || 'unknown',
					status: mediaPageErrorStatus,
				});
			}
		}
	};

	const updatePresentedMedia = useCallback((
		funcProps: MediaStateUpdate,
		doEmitChanges: boolean = true,
		from: string = '',
	) => {
		const videoState = { ...funcProps } as VideoState;
		const {
			volume: volumeUpdate,
			muted: mutedUpdate,
			mediaPage: mediaPageUpdate,
		} = funcProps;

		if (volumeUpdate !== undefined || mutedUpdate !== undefined) {
			// Attach the media ID when it doesn't exist on the passed-in object.
			// Example: the default media videos.
			if (mediaPageUpdate) {
				mediaPageUpdate.mediaId = mediaPageUpdate.mediaId ?? mediaId;
			}

			updatePage(
				volumeUpdate,
				mutedUpdate,
				mediaPageUpdate,
				from,
			);
		}

		if (isEditingFeed && presentedMediaItem) {
			if (videoState.paused === false) {
				videoState.timeStarted = new Date();
			}
			updateShowbuilderMedia(presentedMediaItem, videoState);
		} else {
			dispatch(MediaActions.setPresentedMediaVideoState({ mediaId, doEmitChanges, videoState }));
		}
	}, [mediaId, presentedMediaItem, presentedMedia, showbuilderMediaItem]);

	const canApplySyncedChanges = useMemo(() => {
		if (
			(presentedMedia.length > 0 || showbuilderMediaItem)
			&& (currentPageIsVideo || currentPageIsAudio)
			&& readyForInput) {
			return true;
		}
		return false;
	}, [
		presentedMedia.length,
		showbuilderMediaItem,
		src,
		readyForInput,
		syncVidState,
	]);

	useEffect(() => {
		if ((mediaEnteredCanvas || mediaIsOnCanvas) && canApplySyncedChanges && syncVidState) {
			if (typeof syncVidState.paused !== 'undefined') {
				if (syncVidState.paused && !isForMainVideoEditingComponent) {
					// console.log('feed-media.tsx PAUSE hook b');
					logger.debug('feed-media.tsx PAUSE hook b');
					send(PAUSE, { from: 'hook b' });
				} else if (syncVidState.paused === false) {
					// console.log('feed-media.tsx PAUSE hook b');
					logger.debug('feed-media.tsx PLAY hook c', { currentTime: Number(syncVidState?.timeElapsed) });
					send(PLAY, { from: 'hook c', currentTime: Number(syncVidState?.timeElapsed) });
				}
				if (!syncVidState.paused && (video as any)?.paused) {
					send(PLAY, { from: 'hook d', currentTime: Number(syncVidState?.timeElapsed) });
				}
			}
		}
	}, [syncVidState?.paused, paused, canApplySyncedChanges, mediaEnteredCanvas]);

	useEffect(() => {
		// console.log('CurrentMediaPage volume', currentMediaPageVolume);
		if ((currentMediaPageVolume === 0 || currentMediaPageVolume) && currentMediaPageVolume !== shownVolume) {
			send(VOLUME, {
				shownVolume: currentMediaPageVolume,
				doFireSync: false,
				from: 'hook currentMediaPage.volume',
			});
		}
	}, [currentMediaPageVolume]);

	useEffect(() => {
		const pageMuteState = currentMediaPage?.muted;

		if (pageMuteState !== undefined && pageMuteState !== videoMachineMuted) {
			send(videoMachineMuted ? UNMUTE : MUTE, {
				doFireSync: false,
				from: 'hook currentMediaPage.muted',
			});
		}
	}, [currentMediaPage?.muted]);

	const prevTimeElapsed = usePrevious(syncVidState?.timeElapsed);
	const prevTimeStarted = usePrevious(syncVidState?.timeStarted);
	const isInitialApplicationOfTime = useRef(true);

	useEffect(() => {
		const isFirstStart = syncVidState?.timeStarted === prevTimeStarted;

		if (!canApplySyncedChanges) { return; }
		// if timeElapsed is undefined, there has not been an update and we start from 0 seconds
		if (!syncVidState?.timeElapsed && syncVidState?.timeElapsed !== 0) { return; }
		// if timeelapsed hasn't changed, there's no update to apply unless it's first load
		if (
			!isInitialApplicationOfTime.current
			&& (syncVidState.timeElapsed === prevTimeElapsed
				&& isFirstStart)) { return; }

		isInitialApplicationOfTime.current = false;

		const syncedTimeElapsed = Number(syncVidState.timeElapsed);

		if (elapsed < (syncedTimeElapsed - 1.5)
			|| elapsed > (syncedTimeElapsed + 1.5)
			|| (duration - syncedTimeElapsed) < 1.5) {
			logger.debug('feed-media.tsx TIMECHANGE hook f', { currentTime: syncedTimeElapsed });
			send(TIMECHANGE, { currentTime: syncedTimeElapsed, duration, from: 'hook f' });
		}
	}, [
		syncVidState?.timeElapsed,
		syncVidState?.timeStarted,
		canApplySyncedChanges,
		elapsed,
		isInitialApplicationOfTime,
	]);

	const shouldAutoPlay = useMemo((): boolean => {
		if (roomState === ROOM_STATES.EDITING || isEditingFeed) return false;

		// if the first shot has not started playing yet, this is null
		if (!shotStartTimestampEntity) return false;

		// if syncVidState.paused is true, has been synced and paused previously
		// if syncVidState is undefined, this is initial load/sync for the room
		if (syncVidState?.paused === true) return false;

		// if the video is not on the canvas, don't autoplay
		if (!mediaIsOnCanvas) return false;

		return true;
	}, [roomState, shotStartTimestampEntity, syncVidState, mediaIsOnCanvas]);

	const isAutoDurationVideo = useMemo((): boolean => {
		const { feedType } = getShotFeedVideoInfo(
			mediaId,
			media,
			!!audioOnly,
		);

		return autoDuration && feedType === VideoFeedTypes.SingleFeed;
	}, [
		audioOnly,
		autoDuration,
		media,
		mediaId,
	]);

	/*
		The goal is to keep the video machine context updated with the XState
		context => `elapsed` value and the video duration. We only want this
		when there is a single visible video feed in the current shot.

		We also want to set everything back to 0 when we are not tracking an
		active video, which is what the useEffect cleanup functions are for.

		It probably would have been cleaner to pass this into the state machine,
		but because of the rules of hooks, I think it makes sense to do this
		here instead of `video-machine.ts`. It's also nice to keep everything
		together in one spot.
	*/
	useEffect(() => {
		if (isAutoDurationVideo && !isForMainVideoEditingComponent && mediaIsOnCanvas) {
			videoMachineDispatch({
				type: VideoMachineActionType.SetPlayingOnShotId,
				payload: shotId,
			});
			videoMachineDispatch({
				type: VideoMachineActionType.UpdateDuration,
				payload: duration,
			});
		}

		return () => {
			if (isAutoDurationVideo && !isForMainVideoEditingComponent && mediaIsOnCanvas) {
				videoMachineDispatch({
					type: VideoMachineActionType.SetInitialized,
					payload: false,
				});
			}
		};
	}, [
		shotId,
		duration,
		isAutoDurationVideo,
		videoMachineDispatch,
		mediaIsOnCanvas,
		isForMainVideoEditingComponent,
	]);

	useEffect(() => {
		if (isAutoDurationVideo && !isForMainVideoEditingComponent && mediaIsOnCanvas) {
			videoMachineDispatch({
				type: VideoMachineActionType.UpdateElapsed,
				payload: elapsed,
			});
		}

		return () => {
			if (isAutoDurationVideo && !isForMainVideoEditingComponent && mediaIsOnCanvas) {
				videoMachineDispatch({
					type: VideoMachineActionType.UpdateElapsed,
					payload: 0,
				});
			}
		};
	}, [
		elapsed,
		isAutoDurationVideo,
		videoMachineDispatch,
		mediaIsOnCanvas,
		isForMainVideoEditingComponent,
	]);

	const onLoadStart = () => {
		/*
			Setting volume here since:
			`onLoadStart` happens before `onLoadedData`.
			The goal is the minimize UI jumps.
		*/
		send(VOLUME, {
			shownVolume: currentMediaPageVolume,
			from: 'onLoadStart',
		});
	};

	const onLoadedData = useCallback(() => {
		setReadyForInput(true);
		iterateLoadedMediaCount?.();
		// first user to write state, or forced write on page change
		const shouldFirePlay = (!syncVidState || forceFireSyncOnAutoplay.current) && isOnCanvas;
		const shouldFirePause = !syncVidState;
		logger.debug('feed-media.tsx LOADED onLoadedData');

		send(LOADED, {
			video: mediaElRef.current,
			audio: doEchoCancel
				? echoCancelAudioRef?.current
				: mediaElRef.current,
			updatePresentedMedia,
			adjustedVolume,
			shownVolume: currentMediaPageVolume,
			paused: syncVidState?.paused ?? !shouldAutoPlay,
			muted,
			from: 'loading video',
			doFireSync: shouldFirePause,
			updateMediaControlsContext: addMediaControls,
			mediaId,
			...(currentMediaPage && { currentMediaPage }),
			isForShotEditorModal,
		});
		addMediaControls({
			id: `${mediaId}`,
			state: {
				...(current as any).context as any,
				paused: syncVidState?.paused,
				duration: (mediaElRef.current as any)?.duration ?? (mediaElRef.current as any)?.getDuration?.() ?? 0,
				elapsed: syncVidState?.timeElapsed ?? 0,
				isForShotEditorModal,
			},
			send,
			from: 'onLoadedData',
		});

		send(PAUSE, { from: 'onLoadedData', doFireSync: shouldFirePause });

		if (shouldAutoPlay && mediaIsOnCanvas) {
			forceFireSyncOnAutoplay.current = false;
			logger.debug('feed-media.tsx PLAY autoplay', { doFireSync: shouldFirePlay });
			send(PLAY, { doFireSync: shouldFirePlay, from: 'autoplay' });
		}
	}, [current, mediaIsOnCanvas, shouldAutoPlay]);

	const onPlay = () => {
		if (initialLoad.current) { start.current = window.performance.now(); }
	};

	const onTimeUpdate = () => {
		send(TIMING, { from: 'onTimeUpdate' });
		if (initialLoad.current && start.current) {
			initialLoad.current = false;
			const end = window.performance.now();
			const time = (end - start.current) / 1000;
			send(TIMECHANGE, { currentTime: time + elapsed, duration, from: 'onTimeUpdate2' });
		}
	};

	const onEnded = () => {
		logger.debug('feed-media.tsx END onEnded');
		send(END, { from: 'onEnded' });
		// All clients update shot locally when video ends, but only timing leader updates Redis
		// This prevents race conditions while ensuring smooth local UI updates
		if (
			autoDuration
			&& autoplay
			&& shotStartTimestampEntity
			&& roomState === ROOM_STATES.SHOWTIME
			&& !mediaLeftCanvas // media no longer on canvas should not trigger next shot
		) {
			const currIndex = shots.findIndex(
				(shot: Shot) => shot.shot_id === shotId,
			);
			const nextShot = shots[currIndex + 1];
			if (nextShot) {
				// During an active scheduled stream with no producer, always update Redis
				// Otherwise, only timing leader updates Redis to prevent race conditions.
				const isActiveScheduledStream = scheduledStream?.status
					&& scheduledStreamActiveStates.includes(scheduledStream.status);
				const shouldUpdateRedis = isTimingLeader || isActiveScheduledStream;

				handleSelectShot(nextShot.shot_id, true, null, shouldUpdateRedis);
			}
		}

		// Let the media controls context know the video has ended
		addMediaControls({
			id: `${mediaId}`,
			// @ts-ignore
			state: {
				paused: true,
				isForShotEditorModal,
			},
		});
	};

	const onError = () => {
		logger.error('feed-media.tsx FAIL', { from: 'onError' });
		send(FAIL, { from: 'onError' });
	};

	return {
		onLoadStart,
		onLoadedData,
		onPlay,
		onTimeUpdate,
		onEnded,
		onError,
		elapsed,
		duration,
		muted,
		mediaIsOnCanvas,
		paused,
	};
}
